import React, { useEffect, useState, useMemo } from 'react';
import {
  Container, Typography, Paper, Chip, Skeleton, Alert, Button, Box
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import AdminTable from '../components/shared/AdminTable';
import { skeletonBg } from '../components/skeletonStyles';
import billingApi from '../services/billingApi';
import { formatDateShort as formatDate } from '../utils/formatters';

function statusColor(status) {
  const colors = {
    succeeded: 'success', failed: 'error', refunded: 'default',
    partially_refunded: 'warning', disputed: 'error'
  };
  return colors[status] || 'default';
}

export default function PaymentHistoryPage() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadPayments();
  }, []);

  async function loadPayments() {
    try {
      const data = await billingApi.get('/api/payments');
      setPayments(data.payments);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const columns = useMemo(() => [
    {
      field: 'created_at',
      headerName: 'Date',
      width: 140,
      type: 'date',
      renderCell: (row) => formatDate(row.created_at),
    },
    {
      field: 'product_name',
      headerName: 'Product',
      width: 160,
      renderCell: (row) => row.product_name || '—',
    },
    {
      field: 'net_amount_cents',
      headerName: 'Amount',
      width: 140,
      type: 'numeric',
      renderCell: (row) => (
        <Box>
          <Typography variant="body2">
            ${(row.net_amount_cents / 100).toFixed(2)} {row.currency}
          </Typography>
          {row.refund_amount_cents > 0 && (
            <Typography variant="caption" color="error" display="block">
              Refund: ${(row.refund_amount_cents / 100).toFixed(2)}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      type: 'singleSelect',
      filterOptions: ['succeeded', 'failed', 'refunded', 'partially_refunded', 'disputed'],
      renderCell: (row) => (
        <Chip label={row.status} color={statusColor(row.status)} size="small" />
      ),
    },
    {
      field: 'invoice_url',
      headerName: 'Invoice',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (row) => row.invoice_url ? (
        <Button size="small" startIcon={<DownloadIcon />} href={row.invoice_url} target="_blank">
          Invoice
        </Button>
      ) : null,
    },
  ], []);

  if (loading) {
    // Static header renders during loading too — only the table region swaps
    // when payments arrive (standard rule 1, no layout jump).
    return (
      <Container maxWidth="md" sx={{ py: 4 }} aria-busy="true">
        <Typography variant="h4" gutterBottom>Payment History</Typography>
        <Paper variant="outlined" sx={{ height: 500, p: 2, overflow: 'hidden' }}>
          <Skeleton variant="text" height={40} width="60%" sx={[skeletonBg, { mb: 1 }]} />
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton
              key={i}
              data-testid="payment-row-skeleton"
              variant="rectangular"
              height={40}
              sx={[skeletonBg, { mb: 1, borderRadius: '2px' }]}
            />
          ))}
        </Paper>
        <Box component="span" role="status" sx={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>
          Loading payment history…
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>Payment History</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {payments.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography color="text.secondary">No payments yet</Typography>
        </Box>
      ) : (
        <Paper variant="outlined" sx={{ height: 500 }}>
          <AdminTable
            rows={payments}
            columns={columns}
            defaultSort={[{ field: 'created_at', direction: 'desc' }]}
            defaultPageSize={25}
          />
        </Paper>
      )}
    </Container>
  );
}
